/**
 * First-load disclaimer (SPEC §10). Acknowledged before use, and repeated
 * permanently in the footer.
 *
 * The wording is deliberately concrete about what the model does *not* know,
 * because "use at your own risk" tells a pilot nothing actionable.
 */

const STORAGE_KEY = 'terrain-airflow.disclaimer.v1';

export function hasAcceptedDisclaimer(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'yes';
  } catch {
    return false;
  }
}

export function acceptDisclaimer(): void {
  try {
    localStorage.setItem(STORAGE_KEY, 'yes');
  } catch {
    // Nothing to do — the modal will simply appear again next visit.
  }
}

export function DisclaimerGate({ onAccept }: { onAccept: () => void }) {
  return (
    <div className="disclaimer-backdrop" role="dialog" aria-modal="true" aria-labelledby="dt">
      <div className="disclaimer">
        <h2 id="dt">Before you use this</h2>
        <p className="lead">
          This tool models a simplified atmosphere over a smoothed representation of the
          ground. It is <strong>not a forecast</strong> and not a substitute for local
          knowledge, a site briefing, or looking at the sky.
        </p>
        <ul>
          <li>
            The airflow is a <em>diagnostic</em> model. It conserves mass over the terrain
            but has no momentum equation, so separation, rotor and wave are added as
            empirical rules — educated approximations, not simulations.
          </li>
          <li>
            Terrain comes from public elevation data resampled to tens of metres per cell.
            Trees, buildings, cliffs and small spurs that matter to a wing are invisible
            to it.
          </li>
          <li>
            Nothing here is labelled safe. Zones are described by what the air is modelled
            to be doing — <em>laminar</em>, <em>rough</em>, <em>rotor</em> — and the
            judgement about whether that suits you, your wing and your day is yours.
          </li>
          <li>
            Thermals, convergence, gust fronts and every real-world transient are outside
            the model entirely.
          </li>
        </ul>
        <button className="accept-btn" onClick={onAccept} autoFocus>
          I understand — continue
        </button>
      </div>
    </div>
  );
}

export function FooterDisclaimer({ attribution }: { attribution: string[] }) {
  return (
    <footer className="app-footer">
      <span className="warn-dot" aria-hidden="true" />
      <span className="footer-text">
        Model output, not a forecast. No zone here is “safe” — use local knowledge.
      </span>
      <span className="footer-attrib">{attribution.filter(Boolean).join(' · ')}</span>
    </footer>
  );
}
